import React, { useState, useEffect } from 'react';
import { IoCheckmarkSharp,IoCheckmarkDoneSharp } from "react-icons/io5"; 
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const InquiryHistory = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [deleteLoading, setDeleteLoading] = useState({});

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/inquiry`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    if (!window.confirm('このお問い合わせを削除しますか？この操作は取り消せません。')) {
      return;
    }

    setDeleteLoading(prev => ({ ...prev, [inquiryId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/inquiry/${inquiryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove the deleted inquiry from the list
      setInquiries(prev => prev.filter(inquiry => inquiry._id !== inquiryId));
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      alert('削除に失敗しました。もう一度お試しください。');
    } finally {
      setDeleteLoading(prev => ({ ...prev, [inquiryId]: false }));
    }
  };

  const toggleMessageExpansion = (inquiryId, messageType) => {
    const key = `${inquiryId}-${messageType}`;
    setExpandedMessages(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessage = (message, maxLines = 3) => {
    const lines = message.split('\n');
    const isExpanded = expandedMessages[`${message}-expanded`];
    
    if (lines.length <= maxLines || isExpanded) {
      return (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message}
        </div>
      );
    } else {
      return (
        <div>
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {lines.slice(0, maxLines).join('\n')}
          </div>
          <button
            onClick={() => toggleMessageExpansion(message, 'expanded')}
            style={{
              backgroundColor: 'transparent',
              color: '#007bff',
              border: 'none',
              padding: '5px 0',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
          >
            全文を表示
          </button>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>読み込み中...</div>
      </div>
    );
  }

  if (inquiries.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '20px', color: '#333' }}>お問い合わせ履歴</h2>
        <div style={{ color: '#666', fontSize: '16px' }}>
          まだお問い合わせがありません。
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px', color: '#333' }}>お問い合わせ履歴</h2>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {inquiries.map((inquiry) => (
          <div
            key={inquiry._id}
            style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#fff',
              position: 'relative'
            }}
          >
            {/* Inquiry Header */}
            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '18px' }}>
                  {inquiry.title}
                </h3>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  送信日時: {formatDate(inquiry.created_at)}
                </div>
              </div>
              <button
                onClick={() => handleDeleteInquiry(inquiry._id)}
                disabled={deleteLoading[inquiry._id]}
                style={{
                  backgroundColor: deleteLoading[inquiry._id] ? '#ccc' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  cursor: deleteLoading[inquiry._id] ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  marginLeft: '10px'
                }}
              >
                {deleteLoading[inquiry._id] ? '削除中...' : '削除'}
              </button>
            </div>

            {/* Original Message */}
            <div style={{ 
              marginBottom: '15px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '5px',
              borderLeft: '4px solid #007bff'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                あなたのメッセージ:
              </div>
              {renderMessage(inquiry.message)}
              <span style={{ float: 'right', color: inquiry.is_read ? '#28a745' : '#ffc107' }}>
                {/* {inquiry.is_read ? '✓ 既読' : '● 未読'} */}
                {inquiry.is_read ? <IoCheckmarkDoneSharp style={{fontSize:'18px'}} />  :  <IoCheckmarkSharp style={{fontSize:'18px'}} />}
                </span>
            </div>
            
            {/* Admin Replies */}
            {inquiry.replies && inquiry.replies.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '15px', color: '#333' }}>管理者からの返信:</h4>
                {inquiry.replies.map((reply, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '15px',
                      padding: '15px',
                      backgroundColor: '#e8f4fd',
                      borderRadius: '5px',
                      borderLeft: '4px solid #28a745',
                      marginLeft: '20px'
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>
                        {reply.adminId?.name || '管理者'}:
                      </span>
                      <span style={{ color: '#666', fontSize: '14px', marginLeft: '10px' }}>
                        {formatDate(reply.createdAt)}
                      </span>
                    </div>
                    {renderMessage(reply.reply)}
                  </div>
                ))}
              </div>
            )}
            
            {/* Read Status */}
            <div style={{
              position: 'absolute',
              bottom: '15px',
              right: '20px',
              color: '#666',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InquiryHistory; 